import { useCallback } from "react";
import { pdf } from "@react-pdf/renderer";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { useCompany } from "@/contexts/CompanyContext";
import { StatementDocument } from "@/lib/react-pdf/StatementDocument";
import { buildCustomerPreviewData, reconcileBuckets, type CustomerPreviewInput } from "@/lib/react-pdf/statement-data";
import type { TemplateBuilderConfig } from "@/types/template-builder";
import type { UploadedFile } from "@/lib/email-placeholder-utils";
import type { SendStatementOptions } from "@/components/customers/send-statements-options-modal";
import { useCompanyWithProfile } from "@/hooks/useCompanyWithProfile";
import { fetchOutstandingTransactions, fetchCustomerAgeing, fetchLatestReceipt, fetchAgeingBandsConfig, resolveStatementLogoUrl } from "@/lib/fetch-email-transactions";
import { resolveStatementCurrency } from "@/lib/react-pdf/statement-data";

export function useStatementPdfAttachment(
  statementOptions: SendStatementOptions | null,
  customerDataById: Map<string, CustomerPreviewInput>,
) {
  const { apiBaseUrl, getHeaders } = useApiConfig();
  const { selectedCompany } = useCompany();
  const { data: companyWithProfile } = useCompanyWithProfile(selectedCompany?.id);
  const companyLogoUrl = companyWithProfile?.details?.logoUrl ?? selectedCompany?.logoUrl ?? null;

  return useCallback(
    async (customerId: string): Promise<UploadedFile> => {
      const opts = statementOptions;
      const ps = opts?.fullTemplate?.pdfSettings;
      if (!opts || !ps?.layoutSettings || !ps.contentSettings || !ps.fieldsSettings) {
        throw new Error("Statement options or template PDF settings not available");
      }

      const customer = customerDataById.get(customerId);
      if (!customer) throw new Error(`No customer data for ${customerId}`);

      const apiConfig = { apiBaseUrl, getHeaders };

      const ageingCurrency = resolveStatementCurrency(customer, opts.currencyMode);
      const [transactions, ageing, latestReceipt, ageingBands] = await Promise.all([
        fetchOutstandingTransactions(apiConfig, customerId, opts.excludeDisputed),
        fetchCustomerAgeing(apiConfig, customerId, ageingCurrency),
        fetchLatestReceipt(apiConfig, customerId),
        fetchAgeingBandsConfig(apiConfig),
      ]);

      const { logoUrl: resolvedLogoUrl, logoSource } = await resolveStatementLogoUrl({
        pdfSettings: ps,
        companyLogoUrl,
        apiBaseUrl,
        companyId: selectedCompany?.id,
        getHeaders,
      });

      const config: TemplateBuilderConfig = {
        layoutSettings: ps.layoutSettings,
        contentSettings: {
          ...ps.contentSettings,
          closingText: { enabled: true, content: opts.statementMessage },
        },
        fieldsSettings: ageingBands
          ? {
              ...ps.fieldsSettings,
              agingSummary: {
                ...ps.fieldsSettings.agingSummary,
                buckets: reconcileBuckets(ps.fieldsSettings.agingSummary.buckets, ageingBands),
              },
            }
          : ps.fieldsSettings,
        primaryColor: ps.styling?.primaryColor ?? "#1a5c5c",
        documentFont: ps.styling?.fontFamily ?? "Helvetica",
        logoUrl: resolvedLogoUrl,
        logoSize: ps.logoSize ?? "medium",
        logoSource,
        companyName: selectedCompany?.name ?? ps.companyName ?? "",
        companyEmail: selectedCompany?.email ?? companyWithProfile?.details?.email ?? undefined,
        companyPhone: selectedCompany?.phone ?? companyWithProfile?.details?.phone ?? undefined,
        companyReg: selectedCompany?.companyRegistrationNumber ?? companyWithProfile?.details?.companyRegistrationNumber ?? undefined,
        companyVat: selectedCompany?.vatRegistrationNumber ?? companyWithProfile?.details?.vatNumber ?? undefined,
        companyAddress: companyWithProfile?.details?.addressLine ?? undefined,
        companyCountry: companyWithProfile?.details?.country ?? undefined,
        companyBankName: companyWithProfile?.bankDetails?.[0]?.accountName ?? undefined,
        sortCode: companyWithProfile?.bankDetails?.[0]?.sortCode ?? undefined,
        bankAccountNumber: companyWithProfile?.bankDetails?.[0]?.accountNumber ?? undefined,
        ibanNumber: companyWithProfile?.bankDetails?.[0]?.ibanNumber ?? undefined,
        swiftCode: companyWithProfile?.bankDetails?.[0]?.swiftBicCode ?? undefined,
      };

      const enrichedCustomer = {
        ...customer,
        lastPaymentAmount: latestReceipt?.amount,
        lastPaymentCurrencyAmount: latestReceipt?.currencyAmount,
      };
      const data = buildCustomerPreviewData(config, enrichedCustomer, transactions, ageing, opts.statementDate, opts.currencyMode, ageingBands);
      const blob = await pdf(<StatementDocument config={config} data={data} />).toBlob();

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const base64Content = dataUrl.split(",")[1] ?? "";

      return {
        name: `statement_${customer.accountNumber}.pdf`,
        dataUrl,
        base64Content,
        contentType: "application/pdf",
      };
    },
    [apiBaseUrl, getHeaders, selectedCompany, companyLogoUrl, companyWithProfile, statementOptions, customerDataById],
  );
}
